import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useAtom } from "jotai";
import { themeAtom } from "../../../-Atoms";

// Firebase imports
import { db, updateDoc, doc } from "../../../firebaseConfig";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Editor from "@monaco-editor/react";
import ReactMarkdown from "react-markdown";

const InvoicingNotesContainer = ({
  jobData,
  clientData,
  setHasUnsavedChanges,
}) => {
  const [theme] = useAtom(themeAtom);

  const [jobNotes, setJobNotes] = useState(
    jobData?.invoicingSpecialRequirements || ""
  );
  const [clientNotes, setClientNotes] = useState(
    clientData?.invoicingNotes || ""
  );
  const [editorFocused, setEditorFocused] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [currentEditingJobNotes, setCurrentEditingJobNotes] = useState("");
  const [currentEditingClientNotes, setCurrentEditingClientNotes] = useState("");
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);

  const editorRef = useRef(null);

  const hasUnsavedChanges = () => {
    return (
      currentEditingJobNotes !== jobNotes ||
      currentEditingClientNotes !== clientNotes
    );
  };

  useEffect(() => {
    if (jobData) {
      setJobNotes(jobData?.invoicingSpecialRequirements || "");
    }
    if (clientData) {
      setClientNotes(clientData?.invoicingNotes || "");
    }
  }, [jobData, clientData]);

  useEffect(() => {
    setHasUnsavedChanges?.(editorFocused);
  }, [editorFocused, setHasUnsavedChanges]);

  const saveJobNotes = async (notesToSave) => {
    if (!jobData?.id) return;
    try {
      await updateDoc(doc(db, "JobData", jobData.id), {
        invoicingSpecialRequirements: notesToSave,
      });
    } catch (error) {
      console.error("Error saving job notes:", error);
    }
  };

  const saveClientNotes = async (notesToSave) => {
    if (!clientData?.id) return;
    try {
      await updateDoc(doc(db, "ClientDatabase", clientData.id), {
        invoicingNotes: notesToSave,
      });
    } catch (error) {
      console.error("Error saving client notes:", error);
    }
  };

  const openModal = (tabIndex = 0) => {
    setActiveTab(tabIndex);
    setCurrentEditingJobNotes(jobNotes);
    setCurrentEditingClientNotes(clientNotes);
    setModalOpen(true);
  };


  const handleModalClose = (event, reason) => {
    if (editorFocused) {
      setHasUnsavedChanges?.(true);
      return;
    }
    
    if (hasUnsavedChanges()) {
      setShowUnsavedDialog(true);
    } else {
      setModalOpen(false);
      setHasUnsavedChanges?.(false);
    }
  };

  const handleSave = async () => {
    // Save both job notes and client notes regardless of active tab
    setJobNotes(currentEditingJobNotes);
    setClientNotes(currentEditingClientNotes);
    
    await Promise.all([
      saveJobNotes(currentEditingJobNotes),
      saveClientNotes(currentEditingClientNotes)
    ]);
    
    setModalOpen(false);
    setShowUnsavedDialog(false);
    setHasUnsavedChanges?.(false);
  };

  const handleCancel = () => {
    setCurrentEditingJobNotes(jobNotes);
    setCurrentEditingClientNotes(clientNotes);
    setModalOpen(false);
    setShowUnsavedDialog(false);
    setHasUnsavedChanges?.(false);
  };

  const handleDiscardChanges = () => {
    setCurrentEditingJobNotes(jobNotes);
    setCurrentEditingClientNotes(clientNotes);
    setModalOpen(false);
    setShowUnsavedDialog(false);
    setHasUnsavedChanges?.(false);
  };

  const handleKeepEditing = () => {
    setShowUnsavedDialog(false);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getPreviewStyles = () => ({
    height: "250px",
    border: `1px solid ${
      theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"
    }`,
    borderRadius: "8px",
    overflow: "hidden",
    cursor: "pointer",
    transition: "all 0.2s ease",
    boxShadow:
      theme === "dark"
        ? "0 3px 8px rgba(0,0,0,0.3)"
        : "0 2px 8px rgba(0,0,0,0.1)",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow:
        theme === "dark"
          ? "0 5px 12px rgba(0,0,0,0.4)"
          : "0 5px 12px rgba(0,0,0,0.15)",
      borderColor:
        theme === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)",
    },
  });

  const getMarkdownStyles = () => ({
    height: "100%",
    padding: 2,
    backgroundColor:
      theme === "dark" ? "rgba(20,20,20,0.8)" : "rgba(255,255,255,0.8)",
    overflow: "auto",
    color: theme === "dark" ? "#e0e0e0" : "#303030",
    fontSize: "14px",
    lineHeight: 1.6,
    position: "relative",
    "&::after": {
      content: '"Click to edit"',
      position: "absolute",
      top: "8px",
      right: "8px",
      fontSize: "12px",
      color: theme === "dark" ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)",
      backgroundColor:
        theme === "dark" ? "rgba(0,0,0,0.3)" : "rgba(255,255,255,0.8)",
      padding: "2px 6px",
      borderRadius: "4px",
      opacity: 0,
      transition: "opacity 0.2s ease",
    },
    "&:hover::after": { opacity: 1 },
    "& h1, & h2, & h3, & h4, & h5, & h6": {
      marginTop: "1em",
      marginBottom: "0.5em",
      color: theme === "dark" ? "#f0f0f0" : "#202020",
    },
    "& p": { marginBottom: "1em" },
    "& code": {
      backgroundColor:
        theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
      padding: "2px 4px",
      borderRadius: "3px",
      fontSize: "0.9em",
    },
    "& pre": {
      backgroundColor:
        theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)",
      padding: "12px",
      borderRadius: "4px",
      overflow: "auto",
    },
    "& blockquote": {
      borderLeft: `3px solid ${theme === "dark" ? "#666" : "#ccc"}`,
      paddingLeft: "12px",
      margin: "1em 0",
      fontStyle: "italic",
    },
    "& ul, & ol": { paddingLeft: "1.5em", marginBottom: "1em" },
    "& li": { marginBottom: "0.25em" },
  });

  const getModalStyles = () => ({
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: "95%", sm: "90%", md: "80%" },
    maxWidth: "950px",
    maxHeight: "95vh",
    bgcolor:
      theme === "dark" ? "rgba(28,28,30,0.95)" : "rgba(245,245,247,0.95)",
    boxShadow:
      theme === "dark"
        ? "0 8px 32px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05)"
        : "0 8px 32px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05)",
    p: 3,
    borderRadius: "12px",
    overflow: "auto",
    display: "flex",
    flexDirection: "column",
    gap: 2,
    backdropFilter: "blur(10px)",
  });

  const editorOptions = {
    minimap: { enabled: false },
    lineNumbers: "off",
    wordWrap: "on",
    scrollBeyondLastLine: false,
    fontSize: 14,
    lineHeight: 1.6,
    padding: { top: 12, bottom: 12 },
    folding: false,
    glyphMargin: false,
    lineDecorationsWidth: 0,
    lineNumbersMinChars: 0,
    overviewRulerLanes: 0,
    hideCursorInOverviewRuler: true,
    overviewRulerBorder: false,
    scrollbar: {
      vertical: "auto",
      horizontal: "auto",
      verticalScrollbarSize: 8,
      horizontalScrollbarSize: 8,
    },
  };

  const MotionButton = motion(Button);


  return (
    <>
      <Dialog
        open={showUnsavedDialog}
        onClose={handleKeepEditing}
        aria-labelledby="unsaved-changes-dialog-title"
        aria-describedby="unsaved-changes-dialog-description"
        PaperProps={{
          sx: {
            backgroundColor: theme === "dark" ? "rgba(28,28,30,0.95)" : "rgba(245,245,247,0.95)",
            backdropFilter: "blur(10px)",
          }
        }}
      >
        <DialogTitle 
          id="unsaved-changes-dialog-title"
          sx={{ 
            color: theme === "dark" ? "#e0e0e0" : "#303030",
            fontWeight: 600,
          }}
        >
          Unsaved Changes
        </DialogTitle>
        <DialogContent>
          <DialogContentText 
            id="unsaved-changes-dialog-description"
            sx={{ 
              color: theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.8)",
            }}
          >
            You have unsaved changes in your invoicing notes. What would you like to do?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ gap: 1, p: 2 }}>
          <MotionButton
            onClick={handleKeepEditing}
            variant="outlined"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Keep Editing
          </MotionButton>
          <MotionButton
            onClick={handleDiscardChanges}
            variant="outlined"
            color="error"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Discard Changes
          </MotionButton>
          <MotionButton
            onClick={handleSave}
            variant="contained"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Save Changes
          </MotionButton>
        </DialogActions>
      </Dialog>

      <Modal
        open={modalOpen}
        onClose={handleModalClose}
        aria-labelledby="invoicing-notes-modal-title"
        disableEscapeKeyDown={true}
      >
        <Box sx={getModalStyles()}>
          <Typography
            id="invoicing-notes-modal-title"
            variant="h5"
            component="h2"
            sx={{
              margin: 0,
              color: theme === "dark" ? "#e0e0e0" : "#303030",
              fontWeight: 600,
              mb: 2,
            }}
          >
            Edit Invoicing Notes
          </Typography>

          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="Invoicing notes tabs"
            sx={{
              borderBottom: 1,
              borderColor: theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
              mb: 2,
            }}
          >
            <Tab 
              label="Job Notes" 
              sx={{
                color: theme === "dark" ? "#e0e0e0" : "#303030",
                "&.Mui-selected": {
                  color: theme === "dark" ? "#ffffff" : "#000000",
                },
              }}
            />
            <Tab 
              label="Client Notes" 
              sx={{
                color: theme === "dark" ? "#e0e0e0" : "#303030",
                "&.Mui-selected": {
                  color: theme === "dark" ? "#ffffff" : "#000000",
                },
              }}
            />
          </Tabs>

          <Box
            sx={{
              height: "400px",
              border:
                theme === "dark"
                  ? "1px solid rgba(255,255,255,0.1)"
                  : "1px solid rgba(0,0,0,0.1)",
              borderRadius: "8px",
              overflow: "hidden",
              boxShadow:
                theme === "dark"
                  ? "inset 0 1px 3px rgba(0,0,0,0.2)"
                  : "inset 0 1px 3px rgba(0,0,0,0.05)",
            }}
          >
            <Editor
              height="400px"
              defaultLanguage="markdown"
              value={activeTab === 0 ? currentEditingJobNotes : currentEditingClientNotes}
              onChange={(value) => {
                if (activeTab === 0) {
                  setCurrentEditingJobNotes(value || "");
                } else {
                  setCurrentEditingClientNotes(value || "");
                }
                setHasUnsavedChanges?.(true);
              }}
              onMount={(editor) => {
                editorRef.current = editor;
                setEditorFocused(false);
              }}
              onFocus={() => setEditorFocused(true)}
              onBlur={() => setEditorFocused(false)}
              theme={theme === "dark" ? "vs-dark" : "vs-light"}
              options={editorOptions}
            />
          </Box>

          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}
          >
            <MotionButton
              variant="outlined"
              onClick={handleCancel}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Cancel
            </MotionButton>
            <MotionButton
              variant="contained"
              onClick={handleSave}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Save Changes
            </MotionButton>
          </Box>
        </Box>
      </Modal>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          width: "100%",
          borderRadius: "10px",
          overflow: "hidden",
          backgroundColor:
            theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow:
            theme === "dark"
              ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
              : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
          padding: 1.5,
          mb: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 500,
            color:
              theme === "dark" ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.9)",
            mb: 1,
          }}
        >
          Invoicing Notes
        </Typography>

        <Box sx={getPreviewStyles()} onClick={() => openModal(0)}>
          <Box sx={getMarkdownStyles()}>
            {(jobNotes || clientNotes) ? (
              <Box>
                {jobNotes && (
                  <Box onClick={(e) => { e.stopPropagation(); openModal(0); }}>
                    <ReactMarkdown>{`\n${jobNotes}`}</ReactMarkdown>
                  </Box>
                )}
                {jobNotes && clientNotes && (
                  <Divider sx={{ 
                    my: 2, 
                    borderColor: theme === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)" 
                  }} />
                )}
                {clientNotes && (
                  <Box onClick={(e) => { e.stopPropagation(); openModal(1); }}>
                    <ReactMarkdown>{`\n${clientNotes}`}</ReactMarkdown>
                  </Box>
                )}
              </Box>
            ) : (
              <p
                style={{
                  color:
                    theme === "dark"
                      ? "rgba(255,255,255,0.4)"
                      : "rgba(0,0,0,0.4)",
                }}
              >
                No invoicing notes available. Click to add notes.
              </p>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default InvoicingNotesContainer;
